import type { ReactNode } from "react";
import { AlertTriangle, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth-provider";

export function useComplianceTenant() {
  const { session, isLoading } = useAuth();

  return {
    isLoading,
    tenantId: session?.activeOrganizationId ?? null,
    hasTenant: Boolean(session?.activeOrganizationId),
  };
}

export function ComplianceUnavailableState({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
      <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-[var(--color-muted-foreground)]" />
      <p className="font-medium text-[var(--color-foreground)]">{title}</p>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
      {children ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

export function ComplianceTenantRequiredState() {
  return (
    <ComplianceUnavailableState
      title="Select an active organization first"
      description="Compliance routes require the current organization ID from your auth session. Refresh your session or switch into the organization you want to manage."
    />
  );
}

export function CompliancePageHeader({
  icon,
  title,
  description,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      {icon ?? <Shield className="h-5 w-5 text-[var(--color-primary)]" />}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">{title}</h1>
        {description ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
